"use client";

import React from "react";
import { Mail, Phone, Clock } from "lucide-react";
import { IoLogoWhatsapp } from "react-icons/io5";
import Footer from "../../components/common/Footer";
import Banner from "../../components/common/Banner";
import { BANNER_TWO } from "../../constants/data";
import useAnalytics from "../../hooks/useAnalytics";
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from "../../lib/analyticsEvents";

const ContactPage = () => {
  const { trackEvent } = useAnalytics();

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone Support",
      details: ["+91 9625-99-1431", "Mon-Sat: 9:00 AM - 6:00 PM"],
    },
    {
      icon: Mail,
      title: "Email",
      details: ["support@main10.in", "info@main10.in"],
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: ["Monday - Saturday", "9:00 AM - 6:00 PM IST"],
    },
  ];

  return (
    <div>
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white pt-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Contact us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get in touch with our support team for any questions or assistance
          </p>
        </div>

        {/* Contact Form and Live Chat Section */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Send us a Message
            </h2>
            <form className="space-y-6" onSubmit={(e) => {
              e.preventDefault();
              // Track form submission event
              trackEvent(ANALYTICS_EVENTS.CONTACT_FORM_SUBMIT, {
                [ANALYTICS_PARAMS.FORM_TYPE]: 'contact_us'
              });
              // Form submission logic would go here
            }}>
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="name"
                >
                  Your Name
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none
                    focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="email"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none
                    focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="subject"
                >
                  Subject
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none
                    focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="How can we help?"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="message"
                >
                  Message
                </label>
                <textarea
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none
                    focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Please describe your issue or question..."
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full py-3 px-6 bg-primary text-white rounded-lg font-medium
                  hover:bg-primary/90 transition-colors"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Live Chat Section */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <div className="text-center">
              <IoLogoWhatsapp className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Live Chat Support
              </h2>
              <p className="text-gray-600 mb-8">
                Get instant help from our customer support team through live
                chat
              </p>
              <button
                className="w-full py-3 px-6 bg-primary text-white rounded-lg font-medium
                  hover:bg-primary/90 transition-colors mb-6"
                  onClick={() => {
                    // Track WhatsApp chat button click
                    trackEvent(ANALYTICS_EVENTS.WHATSAPP_CHAT_CLICK, {
                      [ANALYTICS_PARAMS.SOURCE]: 'contact_page'
                    });
                    window.open("https://api.whatsapp.com/send/?phone=919625991431");
                  }}
              >
                Start Live Chat
              </button>
              <p className="text-sm text-gray-500">
                Average response time: &lt; 2 minutes
              </p>
            </div>

            {/* Support Hours */}
            <div className="mt-12 border-t border-gray-100 pt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Chat Support Hours
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Monday - Friday</span>
                  <span>9:00 AM - 8:00 PM IST</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Saturday</span>
                  <span>9:00 AM - 6:00 PM IST</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Sunday</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

         {/* Contact Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 mt-20">
          {contactInfo.map((info, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100
                hover:shadow-md transition-shadow"
            >
              <div className="flex items-center mb-4">
                <info.icon className="w-6 h-6 text-primary mr-3" />
                <h3 className="text-lg font-semibold text-gray-800">
                  {info.title}
                </h3>
              </div>
              <div className="space-y-2">
                {info.details.map((detail, idx) => (
                  <p key={idx} className="text-gray-600">
                    {detail}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Map Section */}
         <div className="relative">
                <Banner
                  heading={BANNER_TWO.heading}
                  description={BANNER_TWO.description}
                />
              </div>
      </div>
    </div>
    <Footer />
    </div>
  );
};

export default ContactPage;
